import { updatePreset, updateSurfacePalette } from '@primevue/themes'
import { StorageSerializers, createGlobalState, useStorage } from '@vueuse/core'
import { computed } from 'vue'

enum ThemeNames {
  Onvi = 'onvi',
  Qonnected = 'qonnected',
}
export const primaryColors = [
  {
    name: ThemeNames.Onvi,
    palette: {
      50: '#fffcea',
      100: '#fff6c5',
      200: '#ffed86',
      300: '#ffde47',
      400: '#ffcb1d',
      500: '#f7a602',
      600: '#e08100',
      700: '#ba5a03',
      800: '#96450a',
      900: '#7c390b',
      950: '#471c01',
    },
  },
  {
    name: ThemeNames.Qonnected,
    palette: {
      50: '#EFEDF7',
      100: '#DCD8EE',
      200: '#B9B0DD',
      300: '#9789CD',
      400: '#7462BC',
      500: '#5745A1',
      600: '#423479',
      700: '#2E2454',
      800: '#1F1839',
      900: '#100C1D',
      950: '#08060E',
    },
  },
]

export interface themeConfig {
  primary: ThemeNames
  surface: string | null
}

export const surfaces = [
  {
    name: 'slate',
    palette: {
      0: '#ffffff',
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
      950: '#020617',
    },
  },
  {
    name: 'gray',
    palette: {
      0: '#ffffff',
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
      950: '#030712',
    },
  },
  {
    name: 'zinc',
    palette: {
      0: '#ffffff',
      50: '#fafafa',
      100: '#f4f4f5',
      200: '#e4e4e7',
      300: '#d4d4d8',
      400: '#a1a1aa',
      500: '#71717a',
      600: '#52525b',
      700: '#3f3f46',
      800: '#27272a',
      900: '#18181b',
      950: '#09090b',
    },
  },
  {
    name: 'neutral',
    palette: {
      0: '#ffffff',
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
      950: '#0a0a0a',
    },
  },
  {
    name: 'stone',
    palette: {
      0: '#ffffff',
      50: '#fafaf9',
      100: '#f5f5f4',
      200: '#e7e5e4',
      300: '#d6d3d1',
      400: '#a8a29e',
      500: '#78716c',
      600: '#57534e',
      700: '#44403c',
      800: '#292524',
      900: '#1c1917',
      950: '#0c0a09',
    },
  },
  {
    name: 'soho',
    palette: {
      0: '#ffffff',
      50: '#f4f4f4',
      100: '#e8e9e9',
      200: '#d2d2d4',
      300: '#bbbcbe',
      400: '#a5a5a9',
      500: '#8e8f93',
      600: '#77787d',
      700: '#616268',
      800: '#4a4b52',
      900: '#34343d',
      950: '#1d1e27',
    },
  },
  {
    name: 'viva',
    palette: {
      0: '#ffffff',
      50: '#f3f3f3',
      100: '#e7e7e8',
      200: '#cfd0d0',
      300: '#b7b8b9',
      400: '#9fa1a1',
      500: '#87898a',
      600: '#6e7173',
      700: '#565a5b',
      800: '#3e4244',
      900: '#262b2c',
      950: '#0e1315',
    },
  },
  {
    name: 'ocean',
    palette: {
      0: '#ffffff',
      50: '#fbfcfc',
      100: '#F7F9F8',
      200: '#EFF3F2',
      300: '#DADEDD',
      400: '#B1B7B6',
      500: '#828787',
      600: '#5F7274',
      700: '#415B61',
      800: '#29444E',
      900: '#183240',
      950: '#0c1920',
    },
  },
]

const defaultPrimary = (appType.value === AppType.Onvi) ? ThemeNames.Onvi : ThemeNames.Qonnected

export const useTheme = createGlobalState(() => {
  const themeConfig = useStorage<themeConfig>('themeConfig', {
    primary: defaultPrimary,
    surface: 'slate',
  }, undefined, { serializer: StorageSerializers.object })

  const resetTheme = () => {
    themeConfig.value.primary = defaultPrimary
    themeConfig.value.surface = 'slate'
  }

  const setPrimary = (value: ThemeNames) => {
    themeConfig.value.primary = value
  }

  const setSurface = (value: string | null) => {
    themeConfig.value.surface = value
  }

  const getPrimary = computed(() => themeConfig.value.primary)

  const getSurface = computed(() => themeConfig.value.surface)

  function getPresetExt() {
    const color = primaryColors.find(c => c.name === themeConfig.value.primary)
    if (!color) {
      throw new Error('Primary color not found')
    }
    return {
      semantic: {
        primary: color.palette,
        colorScheme: {
          light: {
            primary: {
              color: '{primary.500}',
              contrastColor: '#ffffff',
              hoverColor: '{primary.600}',
              activeColor: '{primary.700}',
            },
            highlight: {
              background: '{primary.50}',
              focusBackground: '{primary.100}',
              color: '{primary.700}',
              focusColor: '{primary.800}',
            },
          },
          dark: {
            primary: {
              color: '{primary.400}',
              contrastColor: '{surface.900}',
              hoverColor: '{primary.300}',
              activeColor: '{primary.200}',
            },
            highlight: {
              background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
              focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
              color: 'rgba(255,255,255,.87)',
              focusColor: 'rgba(255,255,255,.87)',
            },
          },
        },
      },
    }
  }

  function getSurfacePalette() {
    const surface = surfaces.find(s => s.name === themeConfig.value.surface)
    if (!surface) {
      throw new Error('Surface color not found')
    }
    return surface.palette
  }

  function updateColors(type: 'primary' | 'surface', color: {
    name: any
    palette: Record<string, string>
  }) {
    if (type === 'primary') {
      setPrimary(color.name)
    }
    else if (type === 'surface') {
      setSurface(color.name)
    }

    applyTheme(type, color)
  }

  function applyTheme(type: 'primary' | 'surface', color: {
    name: ThemeNames
    palette: Record<string, string>
  }) {
    if (type === 'primary') {
      updatePreset(getPresetExt())
    }
    else if (type === 'surface') {
      updateSurfacePalette(color.palette)
    }
  }

  updatePreset(getPresetExt())
  updateSurfacePalette(getSurfacePalette())

  return {
    updateColors,
    themeConfig,
    resetTheme,
    getPrimary,
    getSurface,
    setPrimary,
    setSurface,
  }
})
